import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import PrizesTable from 'components/Dashboard/WorldCup/Prizes/PrizesTable';
import Loading from 'components/Basic/Loading';
import PrizesFormModal from 'components/Dashboard/WorldCup/Prizes/PrizesFormModal';
import PrizesFilter from 'components/Dashboard/WorldCup/Prizes/PrizesFilter';
import CampaignSelector from 'components/Dashboard/WorldCup/CampaignSelector';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetPrizesListQuery } from 'core/worldCup/prizes/PrizesService';
import { setPrizesData } from 'core/worldCup/prizes/PrizesSlice';

const Prizes: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowDetailModal: false,
    searchText: '',
    sortStatus: '',
    filterStatus: '',
    sortName: '',
    sortId: 'desc',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);

  const dispatch = useAppDispatch();
  const [getPrizesList] = useLazyGetPrizesListQuery();

  const { list } = useAppSelector(state => state.prizesReducer);

  useEffect(() => {
    setState(prev => ({ ...prev, searchText: '' }));
  }, []);

  useEffect(() => {
    getInitialData();
  }, [selectedCampaignId]);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setState(prev => ({ ...prev, isLoading: true }));

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      count: true,
      sort_by_id: state.sortId,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizesList({ queryString }).unwrap();
      dispatch(setPrizesData({ list: res }));
    } catch (error) {
      console.error('Error fetching prizes list:', error);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleChangePage = async (page: number, size: number, search = '') => {
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizesList({ queryString }).unwrap();
      dispatch(setPrizesData({ list: res }));
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (e) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState({ ...state, searchText: searchTextValue });
    if (e.keyCode === 13) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        count: true,
        sort_by_status: state.sortStatus,
        sort_by_id: state.sortId,
        sort_by_name: state.sortName,
        filter: state.filterStatus,
        campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
      });

      try {
        const res = await getPrizesList({ queryString }).unwrap();
        dispatch(setPrizesData({ list: res }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleAvailableFilter = (value: string) => {
    setState({ ...state, filterStatus: value });
  };

  const handleSort = (value: string) => {
    let newState = { ...state };
    if (value[0] === 'I') {
      newState = {
        ...newState,
        sortId: value.substring(1),
        sortName: '',
        sortStatus: '',
      };
    } else if (value[0] === 'N') {
      newState = {
        ...newState,
        sortName: value.substring(1),
        sortId: '',
        sortStatus: '',
      };
    } else if (value[0] === 'S') {
      newState = {
        ...newState,
        sortStatus: value.substring(1),
        sortId: '',
        sortName: '',
      };
    }
    setState(newState);
  };

  const onApplyFilter = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizesList({ queryString }).unwrap();
      dispatch(setPrizesData({ list: res }));
    } catch (e) {
      console.log(e);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <div className="prizes_wrapper" id="prizes_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <CampaignSelector
        selectedCampaignId={selectedCampaignId}
        onSelect={setSelectedCampaignId}
      />
      <div className="btn_wrapper" id="new_prize_btn_wrapper">
        <Button
          className="add_btn"
          onClick={() => setState({ ...state, isShowDetailModal: true })}
          id="new_prize_btn"
        >
          <FormattedMessage id="Add new prize" />
          <ArrowForward />
        </Button>
      </div>
      <PrizesFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onAvailableFilter={handleAvailableFilter}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
      />
      <PrizesTable
        prizesData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
        campaignId={selectedCampaignId}
      />
      <PrizesFormModal
        mode="modal"
        isOpenModal={state.isShowDetailModal}
        onCloseModal={() => setState({ ...state, isShowDetailModal: false })}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
        prize={null}
        campaignId={selectedCampaignId}
      />
    </div>
  );
};

Prizes.defaultProps = {};

export default Prizes;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  count = true,
  sort_by_id = 'desc',
  sort_by_status = '',
  sort_by_name = '',
  filter = '',
  campaignId = '',
}) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (campaignId) {
    params.push(`campaignId=${campaignId}`);
  }

  if (sort_by_status) {
    params.push(`sort=hasAmericanVisa`);
    params.push(`sortDirection=${sort_by_status}`);
  }

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort=name`);
    params.push(`sortDirection=${sort_by_name}`);
  }

  if (filter) {
    params.push(`hasAmericanVisa=${filter}`);
  }

  return params.join('&');
};
